import { Injectable, inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { ViewStore } from '@portal/app/shared/state/view.store';
import { DashboardLiteralIds } from '@portal/app/shared/constants/constants';

/**
 * Self-contained context service for the MIA panel.
 *
 * Mirrors the pattern used by geo-designer-v2's QueryParamsService:
 * clientId and brandId come from the global SelectionService signal;
 * dashboardLiteralId is parsed from the current Router URL on every
 * NavigationEnd so the panel's panel-config query key updates automatically
 * when the user navigates between pages. The panel can be hosted in any
 * shell and is self-sufficient — no @Input() wiring required.
 */
@Injectable({ providedIn: 'root' })
export class MiaContextService {
  private readonly selectionService = inject(SelectionService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly viewStore = inject(ViewStore);

  readonly clientId = computed(
    () => this.selectionService.clientBrandSignal().clientId
  );

  readonly brandId = computed(
    () => this.selectionService.clientBrandSignal().brandId
  );

  // Bootstrap-only fallback for dashboardLiteralId; source of truth is
  // ViewStore.currentDashboardLiteralId (see ViewStore for the full contract).
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly dashboardLiteralId = computed(() => {
    const url = this.currentUrl();
    if (!url.includes('/products/')) return '';
    return (
      this.viewStore.currentDashboardLiteralId() ||
      MiaContextService.bootstrapLiteralId(url)
    );
  });

  /**
   * Display name for the current user (shown in the panel welcome heading).
   * Falls back to empty string when auth is not yet resolved.
   */
  readonly userName = computed(
    () => this.authService.getUser()?.getName() ?? ''
  );

  /**
   * True once we have a valid tenant context — gates all panel queries.
   */
  readonly isReady = computed(() => this.clientId() > 0 && this.brandId() > 0);

  // Initial-render fallback only; inter-page navigation is driven by ViewStore.
  private static bootstrapLiteralId(url: string): string {
    // Only accept canonical dashboard ids so non-dashboard product routes
    // (admin/settings, mpo/plans, etc.) aren't misclassified and mis-scope the
    // panel; host components publish their own id to ViewStore on init.
    const known = new Set<string>(Object.values(DashboardLiteralIds));
    const twoSeg = url.match(/\/products\/[^/?#]+\/([^/?#]+)/)?.[1];
    if (twoSeg && known.has(twoSeg)) return twoSeg;
    const oneSeg = url.match(/\/products\/([^/?#]+)/)?.[1];
    return oneSeg && known.has(oneSeg) ? oneSeg : '';
  }
}
